import { Router } from '@angular/router';
import type { EmployeeData } from '@interfaces/hiring-manager.model';
import { NAVIGATION_ROUTES } from './navigation-routes.constants';

export interface MenuClickEvent {
  route?: string;
  item?: {
    route?: string;
    children?: Array<{ route?: string }>;
  };
  triggerItem?: {
    route?: string;
  };
}

export class NavigationHelper {
  private static readonly STORAGE_KEY = 'sideNavHasNavigated';

  constructor(private readonly router: Router) {}

  getDefaultRoute(
    user: EmployeeData | null | undefined,
    validRoutes: string[],
  ): string {
    if (
      user?.isReporties &&
      validRoutes.includes(NAVIGATION_ROUTES.TEAM_MANAGEMENT)
    ) {
      return NAVIGATION_ROUTES.TEAM_MANAGEMENT;
    }

    if (user?.isOpsTeam) {
      const opsRoute = validRoutes.find(
        (route) =>
          route.includes('ops-vendor-management') ||
          route.includes('asset-management'),
      );
      if (opsRoute) {
        return opsRoute;
      }
    }

    if (validRoutes.includes(NAVIGATION_ROUTES.VENDORFLOW_DASHBOARD)) {
      return NAVIGATION_ROUTES.VENDORFLOW_DASHBOARD;
    }

    // Prefer iSkill as the personal-view landing for any CBRE employee who
    // doesn't have reportees / ops duties. Avoids the "land on Hiring Manager
    // with zero counters" case for non-leader employees whose validRoutes
    // list happens to start with /hiring-manager.
    if (validRoutes.includes('/iskill')) {
      return '/iskill';
    }

    const fallbackRoute = validRoutes[0] || NAVIGATION_ROUTES.TEAM_MANAGEMENT;
    return fallbackRoute;
  }

  extractRouteFromEvent(event: MenuClickEvent): string | null {
    return (
      event?.route ||
      event?.item?.route ||
      event?.triggerItem?.route ||
      event?.item?.children?.find((child) => child.route)?.route ||
      null
    );
  }


  isAuthorizedRoute(route: string, role: string): boolean {
    if (role === 'vendor') {
      return (
        route === NAVIGATION_ROUTES.VENDORFLOW_DASHBOARD ||
        route.startsWith('/vendor-management')
      );
    }
    return true;
  }


  navigateIfAuthorized(route: string, role: string): boolean {
    if (this.isAuthorizedRoute(route, role)) {
      this.router.navigate([route]);
      return true;
    }

    console.warn(
      `Unauthorized access attempt to route: ${route} for role: ${role}`,
    );
    return false;
  }

  hasNavigatedThisSession(): boolean {
    return sessionStorage.getItem(NavigationHelper.STORAGE_KEY) === 'true';
  }

  markNavigationComplete(): void {
    sessionStorage.setItem(NavigationHelper.STORAGE_KEY, 'true');
  }
}
